// ============================================================================
// ACTIONS that are shared between Confluence and Jira
// ============================================================================

import { fg } from '@atlaskit/platform-feature-flags';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import {
	generateRovoReceiverId as generateConfluenceReceiverId,
	getConfluenceContentType,
	isConfluenceCreatableAction,
	isConfluencePreviewCardAction,
	isConfluenceStreamAction,
	isConfluenceUpdatableAction,
	isDynamicUiCreatableAction,
} from './confluence-actions';
import {
	generateJiraReceiverId,
	getJiraContentType,
	isJiraAction,
	isJiraCreatableAction,
	isJiraPreviewCardAction,
	isJiraStreamAction,
	isJiraUpdatableAction,
} from './jira-actions';
import {
	generateJsmReceiverId,
	getJsmContentType,
	isJsmAction,
	isJsmCreatableAction,
	isJsmPreviewCardAction,
	isJsmStreamAction,
	isJsmUpdatableAction,
} from './jsm-actions';

export const isRovoAction = (action: unknown): action is MessageAction =>
	typeof action === 'object' && action !== null && 'key' in action;

/** Indicates the action is an AI-First Creation content creation action.
 *  Messages with a creation action will show a preview card in chat which can be loaded by clicking the "Preview" button.
 */
export const isCreatableAction = (action: { key: string }): boolean =>
	isConfluenceCreatableAction(action) ||
	isJiraCreatableAction(action) ||
	isJsmCreatableAction(action) ||
	isDynamicUiCreatableAction(action);

/** Indicates the action is an AI-First Creation content update action.
 *  Messages with an update action will not show anything in chat, but will update the current content in the AIFC staging area, or the currently loaded content in the product.
 */
export const isUpdatableAction = (action: { key: string }): boolean =>
	isConfluenceUpdatableAction(action) ||
	isJiraUpdatableAction(action) ||
	isJsmUpdatableAction(action);

/** Indicates this is any type of AIFC-related action, either a creation or update action.
 */
export const isAIFCAction = (action: { key: string }): boolean =>
	isCreatableAction(action) || isUpdatableAction(action);

export const getAIFCContentType = (action: {
	key: string;
}):
	| 'confluence-page'
	| 'confluence-whiteboard'
	| 'confluence-slide'
	| 'confluence-database'
	| 'confluence-space'
	| 'jira-issue-table'
	| 'jsm-create-survey'
	| 'unknown' => {
	return (
		getConfluenceContentType(action) ??
		getJiraContentType(action) ??
		getJsmContentType(action) ??
		'unknown'
	);
};

/** Indicates the action supports streaming.
 *  Actions with streaming support stream their response back in chunks, rather than waiting for the action to complete.
 */
export const isSupportedStreamAction = (action: { key: string }): boolean =>
	isConfluenceStreamAction(action) || isJiraStreamAction(action) || isJsmStreamAction(action);

export const isPreviewCardAction = (action: { key: string }): boolean =>
	isConfluencePreviewCardAction(action) ||
	isJiraPreviewCardAction(action) ||
	isJsmPreviewCardAction(action) ||
	isDynamicUiCreatableAction(action);

/**
 * Normalizes the receiver ID generation for Rovo actions cross-product based on the provided action and optional identifier.
 *
 * - If the action is not a Rovo action, returns `undefined`.
 * - If the action is a Jira action, delegates to `generateJiraReceiverId`.
 * - Otherwise, delegates to `generateConfluenceReceiverId`.
 *
 * @param action - The action object to generate the receiver ID for.
 * @param identifier - An optional identifier used for Confluence receiver ID generation.
 * @returns The generated receiver ID as a string, or `undefined` if not applicable.
 */
export const generateRovoReceiverId = (
	action: unknown,
	identifier?: string,
): string | undefined => {
	if (!fg('jira_aifc_enabled')) {
		return generateConfluenceReceiverId(action as { key: string }, identifier ?? '');
	}

	if (!isRovoAction(action)) {
		return undefined;
	}

	if (isJiraAction(action)) {
		return generateJiraReceiverId(action);
	}

	if (isJsmAction(action) && fg('surveys-aifc')) {
		return generateJsmReceiverId(action);
	}

	return generateConfluenceReceiverId(action, identifier ?? '');
};

/**
 * Determines if an action is considered static (eligible for the default confirm/cancel controls).
 * We don't want to show confirm/cancel for previewable actions when preview cards are enabled.
 */
export const isStaticAction = ({
	action,
	isContentPreviewSupported,
}: {
	action: MessageAction;
	isContentPreviewSupported?: boolean;
}): boolean => {
	if (fg('aifc_create_enabled')) {
		return (
			!isSupportedStreamAction({ key: action.key }) &&
			!isJiraCreatableAction({ key: action.key }) &&
			!isJsmCreatableAction({ key: action.key })
		);
	}
	if (!isContentPreviewSupported && !fg('aifc_create_enabled')) {
		return !isPreviewCardAction({ key: action.key });
	}

	return true;
};

/**
 * Finds a message by invocation ID.
 * @param messages - The messages to search through.
 * @param invocationId - The invocation ID to search for.
 * @returns The message with the matching invocation ID, or undefined if not found.
 */
export function findMessageByInvocationId(
	messages: StoreChatItem[],
	invocationId: string,
): StoreAssistant | undefined {
	return messages.find(
		(msg): msg is StoreAssistant =>
			isStoreAssistant(msg) &&
			!!msg.content.message.message.actions?.some((action) => action.invocationId === invocationId),
	);
}
